import { useContext } from "react";
import { EntityMentionAnnotationScheme } from "../../annotation/scheme";
import { SituationMentionAnnotationScheme } from "../../annotation/scheme";
import { EntityAnnotationScheme } from "../../annotation/scheme";
import { SituationAnnotationScheme } from "../../annotation/scheme";

import { UUID } from "../../model/uuid";
import { Token } from "../token/token";
import { SpanTree } from "./span-tree";
import { SpanNode } from "./span-tree";
import { MentionModel } from "../../communication";

type MentionAnnotationScheme =
	| EntityMentionAnnotationScheme
	| SituationMentionAnnotationScheme;

type ReferentAnnotationScheme =
	| EntityAnnotationScheme
	| SituationAnnotationScheme;

interface TaggedTokenSpanProps {
	annotationScheme: MentionAnnotationScheme | ReferentAnnotationScheme;
	activeSpanId: UUID | null;
	activeTypeId: UUID | null;
	activeSituation: UUID | null;
	highlightMentions?: Set<string>; 
	tree: SpanTree;
	node: SpanNode;
	nodeMentionModel?: "entity" | "situation";
	onSpanClick: (
		span: SpanNode,
		activeTypeId: UUID | null,
		event?: React.MouseEvent
	) => void;
	onTokenClick: (
		tokenIndex: number,
		typeId: UUID | null,
		e: React.MouseEvent
	) => void;
	metaKeyClicked: boolean;
	shiftKeyClicked: boolean;
	currentMentionModel: MentionModel;
	hoveredSpan: string;
	setHoveredSpan: Function;
}

export function TaggedTokenSpan(props: TaggedTokenSpanProps) {
	const children = props.tree.childrenOf(props.node);

	// const mentionModel = useContext(MentionModelContext);

	const elements = children.map((n, i) =>
		n.kind === "span" ? (
			<TaggedTokenSpan
				key={n.subspanId.uuidString}
				annotationScheme={props.annotationScheme}
				activeSpanId={props.activeSpanId}
				activeTypeId={props.activeTypeId}
				activeSituation={props.activeSituation}
				highlightMentions={props.highlightMentions}
				tree={props.tree}
				node={n}
				nodeMentionModel={n.mentionModel}
				onSpanClick={props.onSpanClick}
				onTokenClick={props.onTokenClick}
				metaKeyClicked={props.metaKeyClicked}
				shiftKeyClicked={props.shiftKeyClicked}
				currentMentionModel={props.currentMentionModel}
				hoveredSpan={props.hoveredSpan}
				setHoveredSpan={props.setHoveredSpan}
			/>
		) : (
			<Token
				key={n.tokenIndex}
				tokenIndex={n.tokenIndex}
				text={n.text}
				onClick={(e: React.MouseEvent) =>
					props.onTokenClick(n.tokenIndex, props.activeTypeId, e)
				}
				metaKeyClicked={props.metaKeyClicked}
				shiftKeyClicked={props.shiftKeyClicked}
				currentMentionModel={props.currentMentionModel}
				hoveredSpan={props.hoveredSpan}
				setHoveredSpan={props.setHoveredSpan}
			/>
		)
	);

	const spanIsActive =
		props.node.kind === "span" &&
		props.node.spanId.uuidString === props.activeSpanId?.uuidString;

	const spanIsActiveSituation =
		props.node.kind === "span" &&
		props.node.spanId.uuidString === props.activeSituation?.uuidString;

	// let spanClassName = spanIsActive ? "token tagged active" : "token tagged";

	let spanClassName = spanIsActiveSituation
		? spanIsActive
			? "token tagged active active-situation"
			: "token tagged active-situation"
		: spanIsActive
		? "token tagged active"
		: "token tagged";

	if (props.metaKeyClicked) {
		if (props.shiftKeyClicked) {
			spanClassName += " join";
		} else {
			spanClassName += " brush";
		}
	}

	if (props.node.mentionModel === "entity") {
		spanClassName += " entity";
	} else {
		spanClassName += " situation";
	}

	if (props.highlightMentions !== undefined && props.highlightMentions.size) {
		if (props.highlightMentions?.has(props.node.spanId.uuidString)) {
			spanClassName += " highlight";
		} else if (
			props.node.spanId.uuidString !== props.activeSituation?.uuidString
		) {
			spanClassName += " unhighlight";
		}
	}

	if (props.hoveredSpan === props.node.spanId.uuidString) {
		spanClassName += " hover";
	}

	if (
		props.currentMentionModel === props.node.mentionModel ||
		props.activeSituation?.uuidString === props.node.spanId.uuidString ||
		(props.annotationScheme.kind === "mention" &&
			props.annotationScheme.subkind === "situation" &&
			props.currentMentionModel === "entity")
	) {
		return (
			<span
				className={spanClassName}
				id={props.node.subspanId.uuidString}
				onClick={(e: React.MouseEvent) =>
					props.onSpanClick(props.node, props.activeTypeId, e)
				}
				onMouseEnter={() => {
					if (props.hoveredSpan !== props.node.spanId.uuidString) {
						props.setHoveredSpan(props.node.spanId.uuidString);
					}
				}}
				onMouseLeave={() => {
					if (props.hoveredSpan !== "") {
						props.setHoveredSpan("");
					}
				}}>
				{elements}
				{props.node.span.ID && `(${props.node.span.ID})  `}
			</span>
		);
	} else {
		return (
			<span
				className={
					props.activeSpanId === null && props.activeTypeId !== null
						? "hidden-span"
						: ""
				}>
				{elements}
			</span>
		);
	}
}
